import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Switch } from 'components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { gptChatService } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import type { ITool, ToolStatus } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { buildUpdateAPIToolPayload, normalizeToolDetails } from './tool-detail-utils';

/** Card status toggle: My Tools API tab only (`type === 'api'`), not MCP or Blocks Tools. */
export function canToggleToolStatus(tool: ITool): boolean {
  if (tool.is_open_tool || tool.type !== 'api') {
    return false;
  }
  return tool.status === 'active' || tool.status === 'inactive';
}

type ToolStatusToggleProps = Readonly<{
  tool: ITool;
  projectKey: string;
  isAdmin: boolean;
  className?: string;
}>;

function stopCardNavigation(event: React.SyntheticEvent) {
  event.stopPropagation();
  event.preventDefault();
}

function useToolStatusToggle({ tool, projectKey, isAdmin }: ToolStatusToggleProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const blocksKey = useConfigStore((state) => state.configurationSettings?.blocksKey || '');
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const isOwner = useIsOwner(tool);

  const canToggle = canToggleToolStatus(tool);
  const canChangeStatus = canToggle && isOwner;
  const isOn = tool.status === 'active';

  const handleCheckedChange = async (checked: boolean) => {
    if (!projectKey || !blocksKey || isUpdating || !canChangeStatus) return;

    const nextStatus: ToolStatus = checked ? 'active' : 'inactive';
    if (nextStatus === tool.status) return;

    setIsUpdating(true);

    try {
      const detailResponse = await gptChatService.getToolById(tool.id, projectKey, blocksKey);
      const detail = normalizeToolDetails(detailResponse, tool);
      if (!detail) {
        throw new Error('Tool details not found');
      }

      const response = await gptChatService.updateAPITool(
        buildUpdateAPIToolPayload(detail, projectKey, nextStatus),
        blocksKey
      );

      if (!response.is_success) {
        throw new Error(response.message || response.detail || 'Failed to update tool status');
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tools'] }),
        queryClient.invalidateQueries({ queryKey: ['tools', 'open-tools'] }),
        queryClient.invalidateQueries({ queryKey: ['tools', 'detail', tool.id, projectKey] }),
      ]);

      toast({
        variant: 'success',
        title: checked
          ? t('TOOL_TURNED_ON') || 'Tool turned on'
          : t('TOOL_TURNED_OFF') || 'Tool turned off',
        description: tool.name,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not update tool',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return { canToggle, canChangeStatus, isOn, isUpdating, handleCheckedChange };
}

export function ToolStatusSwitch({ tool, projectKey, isAdmin, className }: ToolStatusToggleProps) {
  const { t } = useTranslation();
  const { canToggle, canChangeStatus, isOn, isUpdating, handleCheckedChange } = useToolStatusToggle({
    tool,
    projectKey,
    isAdmin,
  });

  if (!canToggle) {
    return null;
  }

  const switchControl = isUpdating ? (
    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
  ) : (
    <Switch
      checked={isOn}
      onCheckedChange={handleCheckedChange}
      disabled={!canChangeStatus}
      aria-label={`${isOn ? 'Turn off' : 'Turn on'} ${tool.name}`}
      className="h-5 w-9 shrink-0 data-[state=checked]:bg-primary [&_span]:size-3.5 [&_span]:data-[state=checked]:translate-x-[18px]"
    />
  );

  return (
    <div
      className={cn('flex h-7 shrink-0 items-center', className)}
      onPointerDown={stopCardNavigation}
      onClick={stopCardNavigation}
    >
      {!canChangeStatus && !isUpdating ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-not-allowed" tabIndex={0}>
                {switchControl}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              {t('YOU_ARE_NOT_THE_OWNER_OF_THIS_TOOL') ||
                'You are not the owner of this tool.'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        switchControl
      )}
    </div>
  );
}
